import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { debounceTime, pairwise } from 'rxjs';
import {
  isSectionStructureObject,
  isSubSectionStructureObject,
  isWidgetStructureObject,
  ModuleStructureObject,
  WidgetMappedType,
} from '../../api/interfaces';
import {
  multiplierAnswerValidator,
  multiplierInitialValue,
  type MultiplierRadioButtonCustomization,
} from '../../components/new-elements/MultiplierRadioButton/multiplier-answer';
import { FormRulesService } from '../rules/form-rules.service';

/**
 * The module structure being compiled and the reactive form built from it.
 *
 * This is the only slice every consumer needs — the builder playground renders
 * a structure with no tender, no applicant and no PEF behind it.
 */
@Injectable()
export class CompilingFormService {
  private readonly fb = inject(FormBuilder);
  private readonly rules = inject(FormRulesService);

  readonly moduleStructure = signal<ModuleStructureObject | null>(null);
  readonly form = this.fb.group<{ [key: string]: FormControl<unknown> }>({});
  /** True once the structure has been walked and every control exists. */
  readonly ready = signal(false);

  constructor() {
    effect(() => {
      const structure = this.moduleStructure();
      if (!structure) return undefined;

      untracked(() => this.buildFrom(structure));
    });

    // `pairwise` swallows the very first emission on purpose — the forced pass
    // at the end of `buildFrom` is what covers the initial state.
    this.form.valueChanges
      .pipe(debounceTime(250), pairwise(), takeUntilDestroyed())
      .subscribe(([previousVal, currentVal]) => {
        this.evaluateRules(previousVal, currentVal);
      });
  }

  /** Registers one control per widget and one rule per showIf/autocomplete. */
  private buildFrom(structure: ModuleStructureObject) {
    const sections = childrenOf(
      structure,
      structure.root.children,
      isSectionStructureObject,
    );

    const subSections = sections.flatMap((section) =>
      childrenOf(structure, section.node.children, isSubSectionStructureObject),
    );

    subSections.forEach((subSection) => {
      const rule = subSection.node.customization.showIfRule;
      if (rule) this.rules.addVisibilityRule(rule, subSection.id);
    });

    const widgets = subSections
      .flatMap((subSection) =>
        childrenOf(
          structure,
          subSection.node.children,
          isWidgetStructureObject,
        ),
      )
      .filter((widget) => widget.node._mappedType !== 'StaticText');

    widgets.forEach((widget) => {
      const customization = widget.node.customization as WidgetCustomization;

      if (customization.showIfRule) {
        this.rules.addVisibilityRule(customization.showIfRule, widget.id);
      }
      // A computed value would overwrite a MultiplierRadioButton's whole
      // `{ option, value }` answer; the builder never sets a rule on it.
      if (
        customization.autocompleteRule &&
        widget.node._mappedType !== 'MultiplierRadioButton'
      ) {
        this.rules.addComputationRule(
          customization.autocompleteRule,
          widget.id,
        );
      }

      this.addElement(
        widget.id,
        initialValueOf(widget.node._mappedType, customization),
        !!customization.required,
        validatorsOf(widget.node._mappedType, customization),
      );
    });

    this.evaluateRules(this.form.value, this.form.value, true);

    this.ready.set(true);
  }

  /** Runs the rule pass and writes every computed value back into its control. */
  private evaluateRules(
    previousVal: Partial<{ [key: string]: unknown }>,
    currentVal: Partial<{ [key: string]: unknown }>,
    force = false,
  ) {
    const updates = this.rules.evaluate(previousVal, currentVal, force);

    // A computation rule owns its target control: whatever it evaluates to is
    // written straight back into the form.
    for (const { rule, value } of updates) {
      this.form.controls[rule.relatedElement as string]?.setValue(value);
    }
  }

  addElement(
    field: string,
    initialValue: unknown,
    required: boolean,
    validators: ValidatorFn[] = [],
  ) {
    this.form.addControl(
      field,
      this.fb.control(
        '',
        required ? [Validators.required, ...validators] : validators,
      ),
    );

    this.form.controls[field].setValue(initialValue);
  }
}

/** Resolves child ids against the structure, keeping only nodes of one kind. */
function childrenOf<T extends ModuleStructureObject[string]>(
  structure: ModuleStructureObject,
  ids: string[],
  is: (node: unknown) => node is T,
): { id: string; node: T }[] {
  const children: { id: string; node: T }[] = [];
  for (const id of ids) {
    const node = structure[id];
    if (is(node)) children.push({ id, node });
  }
  return children;
}

interface WidgetCustomization {
  defaultValue?: unknown;
  required?: boolean;
  showIfRule?: string;
  autocompleteRule?: string;
  groups?: { key: string; defaultValue?: unknown }[];
}

/** The control's first value: the saved answer, in the shape its widget holds. */
function initialValueOf(
  mappedType: WidgetMappedType,
  customization: WidgetCustomization,
): unknown {
  switch (mappedType) {
    case 'MultiRadioButton':
      return multiRadioInitialValue(customization);
    case 'MultiplierRadioButton':
      return multiplierInitialValue(customization.defaultValue);
    default:
      return customization.defaultValue ?? '';
  }
}

/** Checks beyond `required`, for the widgets whose answer has a shape to keep. */
function validatorsOf(
  mappedType: WidgetMappedType,
  customization: WidgetCustomization,
): ValidatorFn[] {
  if (mappedType !== 'MultiplierRadioButton') return [];

  return [
    multiplierAnswerValidator(
      customization as unknown as MultiplierRadioButtonCustomization,
    ),
  ];
}

/**
 * The initial control value for a MultiRadioButton: the saved answer object
 * when present, otherwise an object seeded from each group's configured
 * default, keyed by the group's stable key.
 */
function multiRadioInitialValue(
  customization: WidgetCustomization,
): Record<string, unknown> {
  const saved = customization.defaultValue;
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    return saved as Record<string, unknown>;
  }

  return Object.fromEntries(
    (customization.groups ?? []).map((group) => [
      group.key,
      group.defaultValue ?? null,
    ]),
  );
}
